import { makeRoom, overlaps, removeEmptyRows, type CellRect } from '@casa/layout';
import { ScreenTileSchema, applyStylePreset, parseDashboard, tileFileForm, writeDashboard, type Connection, type Dashboard, type Grid, type Screen, type ScreenTile, type StylePreset, type TileOrigin, type TileStyle } from '@casa/model';
import { catalogEntry, defaultSpan, type CatalogType } from './tile_catalog.ts';

// The edits of edit mode beyond moving and resizing (requirements D-4, L-10 to L-13 and C-2, C-5 in
// docs/specs/dashboard-editor.md), as plain functions on the working copy. A working copy carries,
// for tiles the stored file does not have, where they come from, so that Save writes a copy as the
// file has the original and a new tile in its short form (`writeDashboard`).

export interface Draft {
    readonly dashboard: Dashboard;
    readonly origins: ReadonlyMap<string, TileOrigin>;
}

type Layout = ScreenTile['layout'];

function allTileIds(dashboard: Dashboard) : Set<string> {
    return new Set(dashboard.screens.flatMap((screen) => screen.tiles.map((tile) => tile.id)));
}

/** `base-1`, `base-2`, … whichever is free in the dashboard and not in `taken`. */
export function newTileId(dashboard: Dashboard, base: string, taken: ReadonlySet<string> = new Set()) : string {
    const ids = allTileIds(dashboard);
    let n = 1;
    while (ids.has(`${base}-${n}`) || taken.has(`${base}-${n}`)) {
        n++;
    }

    return `${base}-${n}`;
}

function withScreen(draft: Draft, screenIndex: number, change: (screen: Screen) => Screen, origins = draft.origins) : Draft {
    return {
        dashboard: { ...draft.dashboard, screens: draft.dashboard.screens.map((screen, index) => (index === screenIndex ? change(screen) : screen)) },
        origins,
    };
}

/**
 * After a change on a scrolling grid: the tile at `index` stays, tiles it overlaps are pushed down,
 * and empty rows close (L-9). A fixed grid is left as it is: tiles may overlap there.
 */
function settle(tiles: readonly ScreenTile[], grid: Grid, index: number) : ScreenTile[] {
    if (grid.rows !== undefined) {
        return [...tiles];
    }

    const rects = removeEmptyRows(index >= 0 ? makeRoom(tiles.map((tile) => tile.layout), index) : tiles.map((tile) => tile.layout));

    return tiles.map((tile, at) => {
        const rect = rects[at]!;
        const same = rect.x === tile.layout.x && rect.y === tile.layout.y;

        return same ? tile : { ...tile, layout: { ...tile.layout, x: rect.x, y: rect.y } };
    });
}

/**
 * Where a tile of `span` goes on a screen (L-13): on a fixed grid the first place, row by row, where
 * it overlaps nothing (the top left if there is none); on a scrolling grid below the last tile.
 */
export function firstFreePlace(tiles: readonly ScreenTile[], span: { width: number; height: number }, grid: Grid) : { x: number; y: number } {
    if (grid.rows === undefined) {
        return { x: 1, y: Math.max(1, ...tiles.map((tile) => tile.layout.y + tile.layout.height)) };
    }

    return freePlace(tiles, span, grid.columns, grid.rows) ?? { x: 1, y: 1 };
}

/** On a fixed grid, the first place, row by row, where a tile of `span` overlaps nothing; none if there is none. */
function freePlace(tiles: readonly ScreenTile[], span: { width: number; height: number }, columns: number, rows: number) : { x: number; y: number } | undefined {
    const rects = tiles.map((tile) => tile.layout);
    for (let y = 1; y + span.height - 1 <= rows; y++) {
        for (let x = 1; x + span.width - 1 <= columns; x++) {
            const candidate: CellRect = { width: span.width, height: span.height, x, y };
            if (!rects.some((rect) => overlaps(rect, candidate))) {
                return { x, y };
            }
        }
    }

    return undefined;
}

/**
 * Where a new tile of `span` goes, at what size (L-13): at the first free place, at its size; on a
 * full fixed grid, at the largest smaller size of about its shape that has a free place (the Mac's
 * thermostat tile results, note 3). Where not even one cell is free, at the top left over other
 * tiles, at its size (`covers`).
 */
export function newTilePlace(tiles: readonly ScreenTile[], span: { width: number; height: number }, grid: Grid) : Layout & { covers: boolean } {
    if (grid.rows === undefined) {
        return { ...firstFreePlace(tiles, span, grid), ...span, covers: false };
    }

    for (let width = span.width; width >= 1; width--) {
        const smaller = { width, height: Math.max(1, Math.round((width * span.height) / span.width)) };
        const place = freePlace(tiles, smaller, grid.columns, grid.rows);
        if (place) {
            return { ...place, ...smaller, covers: false };
        }
    }

    return { x: 1, y: 1, ...span, covers: true };
}

/** A tile in its parsed form from the form it is written in. */
function parseTile(json: Readonly<Record<string, unknown>>) : ScreenTile {
    return ScreenTileSchema.parse(json) as ScreenTile;
}

/** A new tile of `type` as the file has it, before it has data: its type's name as its title, its catalog options and style. */
function newTileJson(type: CatalogType, id: string, layout: Layout) : Record<string, unknown> {
    const entry = catalogEntry(type);

    return {
        type,
        id,
        label: entry.name,
        layout,
        ...(entry.options ? { options: entry.options } : {}),
        ...(entry.style ? { style: entry.style } : {}),
    };
}

/** A new tile of `type`, as `addTile` makes it, at the top left: for a preview before it is added. */
export function newTileOf(type: CatalogType, span: { width: number; height: number }) : ScreenTile {
    return parseTile(newTileJson(type, `${type}-new`, { x: 1, y: 1, ...span }));
}

/** The style picked for a new tile (tile-styles.md TY-6), and the size it gives it (TY-10); no preset: the dashboard's. */
export interface NewTileStyle {
    preset: (StylePreset & { readonly id?: string }) | undefined;
    span: { width: number; height: number };
}

/**
 * A new tile of `type`, at the first free place of the screen (L-13): at its type's usual size, or
 * with the style picked and its size; smaller where the screen has no room for it (`newTilePlace`).
 * Returns the new draft, the tile's id, and whether it covers other tiles, as on a full screen.
 */
export function addTile(draft: Draft, screenIndex: number, type: CatalogType, style?: NewTileStyle) : { draft: Draft; id: string; covers: boolean } {
    const { grid } = draft.dashboard;
    const screen = draft.dashboard.screens[screenIndex]!;
    const { covers, ...layout } = newTilePlace(screen.tiles, style?.span ?? defaultSpan(catalogEntry(type), grid), grid);
    const id = newTileId(draft.dashboard, type);
    const json = newTileJson(type, id, layout);
    const { layout: _layout, id: _id, ...written } = json;
    const origins = new Map(draft.origins).set(id, { json: written });
    const tile = style ? applyStylePreset(parseTile(json), style.preset) : parseTile(json);

    return {
        draft: withScreen(draft, screenIndex, (current) => {
            const tiles = [...current.tiles, tile];

            return { ...current, tiles: settle(tiles, grid, tiles.length - 1) };
        }, origins),
        id,
        covers,
    };
}

/** The dashboard's tile style (dashboard-editor spec §5.9, TS-2); nothing set: the defaults. */
export function setTileStyle(draft: Draft, style: TileStyle) : Draft {
    const set = Object.fromEntries(Object.entries(style).filter(([, value]) => value !== undefined));
    const { tileStyle: _tileStyle, ...rest } = draft.dashboard;

    return { ...draft, dashboard: Object.keys(set).length > 0 ? { ...rest, tileStyle: set } : rest };
}

/** Every tile of the dashboard, on every screen, through `change`. */
function withTiles(draft: Draft, change: (tile: ScreenTile) => ScreenTile) : Draft {
    return { ...draft, dashboard: { ...draft.dashboard, screens: draft.dashboard.screens.map((screen) => ({ ...screen, tiles: screen.tiles.map(change) })) } };
}

/** The dashboard's own styles (`tileStyles`, tile-styles.md TY-9), none left out of the file. */
function withOwnStyles(draft: Draft, styles: readonly StylePreset[]) : Draft {
    const { tileStyles: _tileStyles, ...rest } = draft.dashboard;

    return { ...draft, dashboard: styles.length > 0 ? { ...rest, tileStyles: [...styles] } : rest };
}

/**
 * One of the dashboard's own styles saved (TY-9): in place of its type's style named `previous`
 * (itself, or the name it had before a rename), else added after the others. A rename renames it on
 * the tiles of its type that were given it (`styleName`, TY-8).
 */
export function saveOwnStyle(draft: Draft, style: StylePreset, previous = style.name) : Draft {
    const own = draft.dashboard.tileStyles ?? [];
    const at = own.findIndex((each) => each.type === style.type && each.name === previous);
    const saved = withOwnStyles(draft, at >= 0 ? own.map((each, index) => (index === at ? style : each)) : [...own, style]);
    if (previous === style.name) {
        return saved;
    }

    return withTiles(saved, (tile) => (tile.type === style.type && tile.styleName === previous ? { ...tile, styleName: style.name } : tile));
}

/** One of the dashboard's own styles deleted (TY-9): the tiles given it keep their copy (TY-8), without its name. */
export function deleteOwnStyle(draft: Draft, type: ScreenTile['type'], name: string) : Draft {
    const own = draft.dashboard.tileStyles ?? [];
    const kept = withOwnStyles(draft, own.filter((each) => !(each.type === type && each.name === name)));

    return withTiles(kept, (tile) => {
        if (tile.type !== type || tile.styleName !== name) {
            return tile;
        }
        const { styleName: _styleName, ...rest } = tile;

        return rest as ScreenTile;
    });
}

/** The dashboard's background behind its screens (issue #35, DS-3); undefined: the default black. */
export function setBackground(draft: Draft, background: string | undefined) : Draft {
    const { background: _background, ...rest } = draft.dashboard;

    return { ...draft, dashboard: background === undefined ? rest : { ...rest, background } };
}

/**
 * The grid made larger, or its margin and spacing set (DS-2, DS-4). Columns and rows never fewer
 * than `least` (the grid as saved): tiles keep their cells, so they always fit. A margin or spacing
 * left undefined is the app's default.
 */
export function setGrid(draft: Draft, grid: Grid, least: Grid) : Draft {
    const columns = Math.max(grid.columns, least.columns);
    const rows = grid.rows === undefined || least.rows === undefined ? undefined : Math.max(grid.rows, least.rows);
    const next: Grid = {
        columns,
        ...(rows === undefined ? {} : { rows }),
        ...(grid.margin === undefined ? {} : { margin: grid.margin }),
        ...(grid.spacing === undefined ? {} : { spacing: grid.spacing }),
    };

    return { ...draft, dashboard: { ...draft.dashboard, grid: next } };
}

/** A tile changed in place, such as its title or data (E6); where it is stays as it was. */
export function changeTile(draft: Draft, screenIndex: number, tile: ScreenTile) : Draft {
    return withScreen(draft, screenIndex, (screen) => ({ ...screen, tiles: screen.tiles.map((each) => (each.id === tile.id ? { ...tile, layout: each.layout } : each)) }));
}

/** Places to try for a copy beside a tile: right of it, below, left, above. */
function besidePlaces(layout: Layout) : { x: number; y: number }[] {
    return [
        { x: layout.x + layout.width, y: layout.y },
        { x: layout.x, y: layout.y + layout.height },
        { x: layout.x - layout.width, y: layout.y },
        { x: layout.x, y: layout.y - layout.height },
    ];
}

/** A copy of a tile, with a new id, beside it (L-11). */
export function duplicateTile(draft: Draft, screenIndex: number, id: string) : { draft: Draft; id: string } {
    const { grid } = draft.dashboard;
    const screen = draft.dashboard.screens[screenIndex]!;
    const index = screen.tiles.findIndex((tile) => tile.id === id);
    const original = screen.tiles[index];
    if (!original) {
        return { draft, id };
    }

    const newId = newTileId(draft.dashboard, original.type);
    const origins = new Map(draft.origins).set(newId, draft.origins.get(original.id) ?? { copyOf: original.id });

    const fits = (place: { x: number; y: number }) : boolean => {
        const rect = { ...original.layout, ...place };
        const inside = rect.x >= 1 && rect.y >= 1 && rect.x + rect.width - 1 <= grid.columns && (grid.rows === undefined || rect.y + rect.height - 1 <= grid.rows);

        return inside && !screen.tiles.some((tile) => overlaps(tile.layout, rect));
    };
    const place = grid.rows === undefined
        ? { x: original.layout.x, y: original.layout.y + original.layout.height }
        : (besidePlaces(original.layout).find(fits) ?? firstFreePlace(screen.tiles, original.layout, grid));

    const copy: ScreenTile = { ...original, id: newId, layout: { ...original.layout, ...place } };

    return {
        draft: withScreen(draft, screenIndex, (current) => {
            const tiles = [...current.tiles.slice(0, index + 1), copy, ...current.tiles.slice(index + 1)];

            return { ...current, tiles: settle(tiles, grid, index + 1) };
        }, origins),
        id: newId,
    };
}

/** The tiles with these ids taken off the screen (L-11). */
export function deleteTiles(draft: Draft, screenIndex: number, ids: ReadonlySet<string>) : Draft {
    const { grid } = draft.dashboard;

    return withScreen(draft, screenIndex, (screen) => ({ ...screen, tiles: settle(screen.tiles.filter((tile) => !ids.has(tile.id)), grid, -1) }));
}

/**
 * A tile drawn above the others (`front`) or below them (`back`), by its place in the screen's list
 * (L-8). Groups are always drawn below every other tile, whatever their place.
 */
export function reorderTile(draft: Draft, screenIndex: number, id: string, to: 'front' | 'back') : Draft {
    return withScreen(draft, screenIndex, (screen) => {
        const tile = screen.tiles.find((candidate) => candidate.id === id);
        if (!tile) {
            return screen;
        }

        const others = screen.tiles.filter((candidate) => candidate.id !== id);

        return { ...screen, tiles: to === 'front' ? [...others, tile] : [tile, ...others] };
    });
}

/** A tile moved to another screen, at the first free place there (L-12). */
export function moveToScreen(draft: Draft, from: number, id: string, to: number) : Draft {
    const { grid } = draft.dashboard;
    const tile = draft.dashboard.screens[from]?.tiles.find((candidate) => candidate.id === id);
    const target = draft.dashboard.screens[to];
    if (!tile || !target || from === to) {
        return draft;
    }

    const moved = { ...tile, layout: { ...tile.layout, ...firstFreePlace(target.tiles, tile.layout, grid) } };
    const removed = deleteTiles(draft, from, new Set([id]));

    return withScreen(removed, to, (screen) => {
        const tiles = [...screen.tiles, moved];

        return { ...screen, tiles: settle(tiles, grid, tiles.length - 1) };
    });
}

/** `base`, or `base-2`, `base-3`, … whichever is not in `taken`. */
function freeName(base: string, taken: (name: string) => boolean) : string {
    let name = base;
    for (let n = 2; taken(name); n++) {
        name = `${base}-${n}`;
    }

    return name;
}

const sameConnection = (a: Connection, b: Connection) : boolean => a.driver === b.driver && JSON.stringify(a.config) === JSON.stringify(b.config);

/**
 * Where a tile copied in from another dashboard goes (L-14): on a scrolling grid below the last tile,
 * square and as wide as the grid; on a fixed grid at the top left, at its own size where it fits.
 */
function copiedLayout(tiles: readonly ScreenTile[], layout: Layout, grid: Grid) : Layout {
    if (grid.rows === undefined) {
        return { x: 1, y: firstFreePlace(tiles, layout, grid).y, width: grid.columns, height: grid.columns };
    }

    return { x: 1, y: 1, width: Math.min(layout.width, grid.columns), height: Math.min(layout.height, grid.rows) };
}

/**
 * A tile of another dashboard, with the connections it reads there, copied onto a screen of
 * `target` (L-14, §4.3). A connection the target has already (the same driver and configuration,
 * whatever its name) is reused; another is added, under a free name if its own is taken. Returns the
 * target as changed, the copy's id, and its origin for `writeDashboard`: the copy as a file writes it.
 */
export function copyTileInto(target: Dashboard, screenIndex: number, tile: ScreenTile, connections: Readonly<Record<string, Connection>>) : { dashboard: Dashboard; id: string; origins: Map<string, TileOrigin> } {
    const screen = target.screens[screenIndex];
    if (!screen) {
        throw new Error(`The dashboard has no screen ${screenIndex + 1}.`);
    }

    const added: Record<string, Connection> = {};
    const renamed = new Map<string, string>();
    const has = (name: string) : boolean => Object.hasOwn(target.connections, name) || Object.hasOwn(added, name);
    if (tile.type !== 'group') {
        for (const source of Object.values(tile.dataSources)) {
            const connection = connections[source.connection];
            if (!connection || renamed.has(source.connection)) {
                continue;
            }

            const existing = Object.entries(target.connections).find(([, candidate]) => sameConnection(candidate, connection))?.[0];
            const name = existing ?? freeName(source.connection, has);
            if (!existing) {
                added[name] = connection;
            }
            renamed.set(source.connection, name);
        }
    }

    const id = allTileIds(target).has(tile.id) ? newTileId(target, tile.type) : tile.id;
    const layout = copiedLayout(screen.tiles, tile.layout, target.grid);
    const copy: ScreenTile = tile.type === 'group'
        ? { ...tile, id, layout }
        : {
            ...tile,
            id,
            layout,
            dataSources: Object.fromEntries(Object.entries(tile.dataSources).map(([key, source]) => [key, { ...source, connection: renamed.get(source.connection) ?? source.connection }])),
        };
    const tiles = [...screen.tiles, copy];

    return {
        dashboard: {
            ...target,
            connections: { ...target.connections, ...added },
            screens: target.screens.map((each, index) => (index === screenIndex ? { ...each, tiles: settle(tiles, target.grid, tiles.length - 1) } : each)),
        },
        id,
        origins: new Map([[id, { json: tileFileForm(copy) }]]),
    };
}

/** Whether a dashboard on `from` is copied to `to` as it is (settings DB-3a): the same kind of grid, of the same size. */
export function isSameGrid(from: Grid, to: Grid) : boolean {
    return from.columns === to.columns && from.rows === to.rows;
}

/**
 * A dashboard laid out again on another grid, for a new dashboard made from it (settings DB-3a,
 * issue #9). Each screen's tiles are taken top to bottom, left to right. On a scrolling grid each is
 * as wide as the grid and half as high (rounded up), stacked; on a fixed grid each keeps its size
 * (made smaller only where the grid is) at the first free place. Tiles without one are staggered
 * (the owner, 2026-10-19): each a cell right of and below the one before, from the top left; where
 * the next would leave the grid, again from the top row, a column further right each time. Groups
 * are left out: a box laid under tiles means nothing once they are laid out again.
 */
export function relayout(dashboard: Dashboard, grid: Grid) : Dashboard {
    const screens = dashboard.screens.map((screen) => {
        const tiles = screen.tiles
            .filter((tile) => tile.type !== 'group')
            .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);
        const placed: ScreenTile[] = [];
        // Where the next staggered tile goes, and the column its diagonal started from.
        let stagger = { x: 1, y: 1, start: 1 };
        for (const tile of tiles) {
            let layout: Layout;
            if (grid.rows === undefined) {
                const height = Math.ceil(grid.columns / 2);
                layout = { x: 1, y: 1 + placed.length * height, width: grid.columns, height };
            } else {
                const rows = grid.rows;
                const span = { width: Math.min(tile.layout.width, grid.columns), height: Math.min(tile.layout.height, rows) };
                let place = freePlace(placed, span, grid.columns, rows);
                if (!place) {
                    const fits = (x: number, y: number) : boolean => x + span.width - 1 <= grid.columns && y + span.height - 1 <= rows;
                    if (!fits(stagger.x, stagger.y)) {
                        const start = fits(stagger.start + 1, 1) ? stagger.start + 1 : 1;
                        stagger = { x: start, y: 1, start };
                    }
                    place = { x: stagger.x, y: stagger.y };
                    stagger = { ...stagger, x: stagger.x + 1, y: stagger.y + 1 };
                }
                layout = { ...place, ...span };
            }
            placed.push({ ...tile, layout });
        }

        return { ...screen, tiles: placed };
    });

    return { ...dashboard, grid, screens };
}

/**
 * The file of a new dashboard on `grid` made from the dashboard file `text` (settings DB-3a): the
 * file as it is on the same grid; on another, its tiles laid out again (`relayout`), and everything
 * else as the file has it.
 */
export function dashboardFileFrom(text: string, grid: Grid) : string {
    const parsed = parseDashboard(text);
    if (!parsed.ok) {
        throw new Error(`The dashboard to copy from is not valid: ${parsed.issues.join('; ')}`);
    }
    if (isSameGrid(parsed.dashboard.grid, grid)) {
        return text;
    }

    const file = JSON.parse(writeDashboard(text, relayout(parsed.dashboard, grid))) as Record<string, unknown>;
    file.grid = grid.rows === undefined ? { columns: grid.columns } : { columns: grid.columns, rows: grid.rows };

    return `${JSON.stringify(file, null, 2)}\n`;
}

// Screens (D-4). A screen is named by the taps that go to it (tiles spec TA-5): names are kept
// unique, and a rename or a delete carries over to those taps.

/** Whether no screen but the one at `except` has the name `name`. */
export function isScreenNameFree(dashboard: Dashboard, name: string, except?: number) : boolean {
    return !dashboard.screens.some((screen, index) => index !== except && screen.name === name);
}

/** `Screen <n>` for a new screen, from the number it gets on, the first that no screen has. */
export function newScreenName(dashboard: Dashboard) : string {
    let number = dashboard.screens.length + 1;
    while (!isScreenNameFree(dashboard, `Screen ${number}`)) {
        number++;
    }

    return `Screen ${number}`;
}

/** How many tiles have a tap that goes to the screen `name`. */
export function tapsTo(dashboard: Dashboard, name: string) : number {
    return dashboard.screens.flatMap((screen) => screen.tiles).filter((tile) => tile.type !== 'group' && tile.tap?.kind === 'screen' && tile.tap.screen === name).length;
}

/** The taps to the screen `from` going to `to` instead, or taken away. */
function retargetTaps(dashboard: Dashboard, from: string, to: string | undefined) : Dashboard {
    const retarget = (tile: ScreenTile) : ScreenTile => {
        if (tile.type === 'group' || tile.tap?.kind !== 'screen' || tile.tap.screen !== from) {
            return tile;
        }
        if (to === undefined) {
            const { tap: _tap, ...rest } = tile;

            return rest as ScreenTile;
        }

        return { ...tile, tap: { ...tile.tap, screen: to } };
    };

    return { ...dashboard, screens: dashboard.screens.map((screen) => ({ ...screen, tiles: screen.tiles.map(retarget) })) };
}

export function addScreen(draft: Draft, name: string, icon: string | undefined) : Draft {
    const screen: Screen = { name, ...(icon ? { icon } : {}), tiles: [] };

    return { ...draft, dashboard: { ...draft.dashboard, screens: [...draft.dashboard.screens, screen] } };
}

/** A screen's name or icon changed. A name another screen has, or none, is not taken; the taps to a renamed screen follow it. */
export function changeScreen(draft: Draft, screenIndex: number, change: { name?: string; icon?: string | undefined }) : Draft {
    const before = draft.dashboard.screens[screenIndex];
    if (!before) {
        return draft;
    }

    const name = change.name !== undefined && change.name !== '' && isScreenNameFree(draft.dashboard, change.name, screenIndex) ? change.name : before.name;
    const changed = withScreen(draft, screenIndex, (screen) => {
        const next: Screen = { ...screen, name };
        if ('icon' in change) {
            if (change.icon) {
                next.icon = change.icon;
            } else {
                delete next.icon;
            }
        }

        return next;
    });
    // Where screens share a name (an older file), the taps stay with the one that keeps it.
    if (name === before.name || !isScreenNameFree(changed.dashboard, before.name)) {
        return changed;
    }

    return { ...changed, dashboard: retargetTaps(changed.dashboard, before.name, name) };
}

/** A screen moved one place up (-1) or down (+1) in the side menu. */
export function moveScreen(draft: Draft, screenIndex: number, by: -1 | 1) : Draft {
    const screens = [...draft.dashboard.screens];
    const to = screenIndex + by;
    if (to < 0 || to >= screens.length) {
        return draft;
    }

    [screens[screenIndex], screens[to]] = [screens[to]!, screens[screenIndex]!];

    return { ...draft, dashboard: { ...draft.dashboard, screens } };
}

/** A screen deleted, unless it is the last one (D-4), and the taps that went to it with it. */
export function deleteScreen(draft: Draft, screenIndex: number) : Draft {
    const deleted = draft.dashboard.screens[screenIndex];
    if (!deleted || draft.dashboard.screens.length <= 1) {
        return draft;
    }

    const dashboard = { ...draft.dashboard, screens: draft.dashboard.screens.filter((_, index) => index !== screenIndex) };

    return { ...draft, dashboard: isScreenNameFree(dashboard, deleted.name) ? retargetTaps(dashboard, deleted.name, undefined) : dashboard };
}

/** The tiles that read from connection `name`, with the screen each is on (C-1, C-5). */
export function tilesUsing(dashboard: Dashboard, name: string) : { screen: string; tile: ScreenTile }[] {
    const using: { screen: string; tile: ScreenTile }[] = [];
    for (const screen of dashboard.screens) {
        for (const tile of screen.tiles) {
            if (tile.type !== 'group' && Object.values(tile.dataSources).some((source) => source.connection === name)) {
                using.push({ screen: screen.name, tile });
            }
        }
    }

    return using;
}

/** Adds connection `name`, or replaces it (C-2). */
export function setConnection(draft: Draft, name: string, connection: Connection) : Draft {
    return { ...draft, dashboard: { ...draft.dashboard, connections: { ...draft.dashboard.connections, [name]: connection } } };
}

/** Renames a connection, in its place among the others, and in every tile that reads from it. */
export function renameConnection(draft: Draft, from: string, to: string) : Draft {
    if (from === to) {
        return draft;
    }

    const connections = Object.fromEntries(Object.entries(draft.dashboard.connections).map(([name, connection]) => [name === from ? to : name, connection]));
    const readsFrom = (tile: ScreenTile) : boolean => tile.type !== 'group' && Object.values(tile.dataSources).some((source) => source.connection === from);
    // Screens and tiles that do not read from it stay the same objects, so that they are not drawn again.
    const screens = draft.dashboard.screens.map((screen) =>
        !screen.tiles.some(readsFrom)
            ? screen
            : {
                ...screen,
                tiles: screen.tiles.map((tile) =>
                    tile.type === 'group' || !readsFrom(tile)
                        ? tile
                        : { ...tile, dataSources: Object.fromEntries(Object.entries(tile.dataSources).map(([key, source]) => [key, source.connection === from ? { ...source, connection: to } : source])) },
                ),
            },
    );

    return { ...draft, dashboard: { ...draft.dashboard, connections, screens } };
}

/** Deletes a connection; tiles that read from it keep their sources and show no data until they get another (C-5). */
export function deleteConnection(draft: Draft, name: string) : Draft {
    const { [name]: _removed, ...connections } = draft.dashboard.connections;

    return { ...draft, dashboard: { ...draft.dashboard, connections } };
}
